import { Component, Input, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';

@Component({
  selector: 'app-image-zoom',
  templateUrl: './image-zoom.component.html',
  styleUrls: ['./image-zoom.component.scss']
})
export class ImageZoomComponent implements OnInit, OnDestroy {
  @Input() imageUrl: string = '';
  @Input() imageAlt: string = '';
  @Input() zoomLevel: number = 2; // Factor de ampliación
  @Input() originalWidth: number = 0; // Ancho real de la imagen original
  @Input() originalHeight: number = 0; // Alto real de la imagen original
  @Input() magnifiedImageUrl: string = ''; // URL de alta resolución para el magnifier

  @ViewChild('originalImage', { static: false }) originalImage: ElementRef;
  @ViewChild('magnifier', { static: false }) magnifier: ElementRef;
  @ViewChild('magnifiedImage', { static: false }) magnifiedImage: ElementRef;

  // Propiedades de la lupa
  magnifierSize: number = 300; // Tamaño del cuadrado de la lupa (más grande)
  isActive: boolean = false;
  mouseX: number = 0;
  mouseY: number = 0;

  // Propiedades de la imagen
  imageRect: DOMRect | null = null;
  imageLoaded: boolean = false;

  constructor(private elementRef: ElementRef) {}

  // Obtener la URL de la imagen para el magnifier
  get magnifierImageUrl(): string {
    return this.magnifiedImageUrl || this.imageUrl;
  }

  ngOnInit(): void {
    this.setupImageLoad();
  }

  ngOnDestroy(): void {
    // Cleanup si es necesario
  }

  setupImageLoad(): void {
    // La imagen ya está cargada cuando se llama este método desde el template
    this.imageLoaded = true;
    setTimeout(() => {
      this.updateImageRect();
    }, 0);
  }

  private updateImageRect(): void {
    if (this.originalImage && this.originalImage.nativeElement) {
      this.imageRect = this.originalImage.nativeElement.getBoundingClientRect();
    }
  }

  // Activar la lupa
  onMouseEnter(): void {
    if (this.imageLoaded) {
      this.isActive = true;
      this.updateImageRect();
    }
  }

  // Desactivar la lupa
  onMouseLeave(): void {
    this.isActive = false;
  }

  // Mover la lupa siguiendo el mouse
  onMouseMove(event: MouseEvent): void {
    if (!this.isActive || !this.imageRect || !this.originalImage) return;

    // Obtener dimensiones exactas según el container CSS
    const imageElement = this.originalImage.nativeElement;
    const imageDisplayWidth = imageElement.clientWidth;
    const imageDisplayHeight = imageElement.clientHeight;

    // Determinar las dimensiones exactas según la orientación
    const isVertical = imageDisplayHeight > imageDisplayWidth;
    const exactWidth = isVertical ? 168 : 256;
    const exactHeight = isVertical ? 256 : 168;

    // Calcular la posición del mouse relativa a la imagen
    const rect = this.imageRect;
    this.mouseX = event.clientX - rect.left;
    this.mouseY = event.clientY - rect.top;

    // Usar las dimensiones del contenedor completo para la detección
    // pero las dimensiones exactas para el cálculo del zoom
    const containerWidth = rect.width;
    const containerHeight = rect.height;

    // Asegurar que el mouse esté dentro de los límites del contenedor completo
    if (this.mouseX >= 0 && this.mouseX <= containerWidth &&
        this.mouseY >= 0 && this.mouseY <= containerHeight) {

      // Solo actualizar el contenido de la lupa, no su posición
      this.updateMagnifiedImage();
    }
  }


  private updateMagnifiedImage(): void {
    if (!this.magnifiedImage || !this.imageRect || !this.originalImage) return;

    const magnifiedElement = this.magnifiedImage.nativeElement;
    const imageElement = this.originalImage.nativeElement;

    // Obtener las dimensiones del contenedor completo
    const containerWidth = this.imageRect.width;
    const containerHeight = this.imageRect.height;

    // Obtener las dimensiones exactas según el container CSS (miniatura)
    const imageDisplayWidth = imageElement.clientWidth;
    const imageDisplayHeight = imageElement.clientHeight;

    // Determinar la orientación y dimensiones de la miniatura
    const isVertical = imageDisplayHeight > imageDisplayWidth;
    const thumbnailWidth = isVertical ? 168 : 256;
    const thumbnailHeight = isVertical ? 256 : 168;

    // Para thumbnails, usamos las mismas dimensiones que la miniatura (no las originales altas)
    // Esto es porque ahora portada usa thumbs igual que fotografía
    const originalWidth = thumbnailWidth;
    const originalHeight = thumbnailHeight;

    // Mapear la posición del mouse desde el contenedor completo a la miniatura
    const imageOffsetX = (containerWidth - thumbnailWidth) / 2;
    const imageOffsetY = (containerHeight - thumbnailHeight) / 2;

    // Ajustar la posición del mouse relativa a la miniatura
    const adjustedMouseX = this.mouseX - imageOffsetX;
    const adjustedMouseY = this.mouseY - imageOffsetY;

    // Calcular la posición relativa dentro de la miniatura (0-1)
    const relativeX = Math.max(0, Math.min(1, adjustedMouseX / thumbnailWidth));
    const relativeY = Math.max(0, Math.min(1, adjustedMouseY / thumbnailHeight));

    // Calcular el offset usando las dimensiones de la thumbnail con zoom
    const offsetX = -(relativeX * thumbnailWidth * this.zoomLevel - this.magnifierSize / 2);
    const offsetY = -(relativeY * thumbnailHeight * this.zoomLevel - this.magnifierSize / 2);

    magnifiedElement.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${this.zoomLevel})`;
    magnifiedElement.style.transformOrigin = 'top left';
  }

  // Prevenir context menu
  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }

  // Actualizar las dimensiones cuando la ventana cambie de tamaño
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.imageLoaded) {
      this.updateImageRect();
    }
  }
}